import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button, Typo, ListTable, Flex, Icon, theme, SubPage, IconAsButton, SwitchRow } from '../../../ui';
import { useCourse, useCourseQuizPages, useUpdateCourse, useSetCourse, useUpdateQuizSettings } from '../hooks';
import QuizEdit from '../../quizes/QuizEdit';
import { useQuizesInfo } from '../../quizes/hooks/useQuizesState';
import { useQuizesActions } from '../../quizes/hooks/useQuizesActions';
import { durationLike, toRelativeCalendar } from '../../../helpers/format';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';
import QuizResultsModal from './QuizResultsModal';
import PageControls from '../../../ui/Page/PageControls';

const QuizInfo = styled.div`
  padding: 2rem 2rem;
`;

const DeleteButton = styled.div`
  text-align: right;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(props) => props.theme.colors.NEU_300};
  border-radius: 4px;
  padding: 0.9rem 0.9rem 0 0.9rem;
  align-items: ${({ align }) => align};
  margin: 0 10px;
  box-shadow: ${({ selected }) => (selected ? `${theme.shadows.BOX_SHADOW};` : 'undefined;')}
  cursor: ${({ $hasAction }) => ($hasAction ? 'pointer;' : 'undefined;')};
`;

const QuizCardsContainer = styled.div`
  display: flex;
  margin: 50px 0;
`;

const CourseQuiz = () => {
  const { t } = useTranslation();

  const course = useCourse();
  const quizPages = useCourseQuizPages();
  const updateCourse = useUpdateCourse();
  const updateQuizSettings = useUpdateQuizSettings();
  const { courseId: courseIdParam } = useParams();
  const effectiveCourseId = course?.id || (courseIdParam ? Number(courseIdParam) : undefined);
  const setCourseState = useSetCourse();

  const { resetUserQuiz, deleteQuizByPage, createQuiz } = useQuizesActions();

  const { quizesInfo: quizes } = useQuizesInfo(effectiveCourseId);

  // State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [useQuizProgress, setUseQuizProgress] = useState(false);
  const [hasQuiz, setHasQuiz] = useState(false);
  const [quizInfo, setQuizInfo] = useState({});
  const [quizAction, setQuizAction] = useState('');
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [tableInfo, setTableInfo] = useState({});
  const [userAnswers, setUserAnswers] = useState(null);
  const [userToReset, setUserToReset] = useState(null);
  const [quizPageToDelete, setQuizPageToDelete] = useState(null);
  const [localQuizes, setLocalQuizes] = useState([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Sync localQuizes with quizes
  // Use JSON.stringify to compare content, not reference
  const quizesString = JSON.stringify(quizes);
  useEffect(() => {
    if (!hasLocalChanges) {
      setLocalQuizes(quizes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizesString, hasLocalChanges]); // Use stringified version to avoid reference changes

  // Sync quiz settings from course
  useEffect(() => {
    const { settings } = course;

    if (settings?.hasOwnProperty('quiz')) {
      const { quiz } = settings;

      setUseQuizProgress(quiz.diplomaByQuizProgress);
      setHasQuiz(quiz.enabled);
    }
  }, [course]);

  // Callback to set table info
  const setTableInfoWithQuiz = useCallback(
    (quiz) => {
      setTableInfo([
        { label: t('globals.user'), hasSorting: false, data: (user) => user.name },
        { label: t('globals.finished'), hasSorting: false, data: (user) => toRelativeCalendar(user.statistics && user.endTime) },
        { label: t('globals.status'), hasSorting: false, data: (user) => (user.status === 'QUIZ_SUCCESS' ? t('globals.success') : t('globals.failed')) },
        { label: t('globals.score'), hasSorting: false, data: (user) => user.statistics && user.score },
        { label: t('globals.duration'), hasSorting: false, data: (user) => durationLike(user.statistics && user.duration) },
        {
          label: 'Actions',
          menuOptions: () => [
            { label: t('course.seeTaskAnswers'), onClick: (item) => setUserAnswers(item) },
            { label: t('course.clearTaskAnswers'), onClick: (item) => setUserToReset(item) },
          ],
        },
      ]);
    },
    [t],
  );

  // Callbacks and handlers
  const handleToggleDialog = useCallback(
    (quizArg) => {
      quizArg?.hasOwnProperty('type') && setQuizAction(quizArg.type);
      setIsDialogOpen(!isDialogOpen);
      quizArg?.hasOwnProperty('quizId') && setQuizInfo(localQuizes.find((quiz) => quiz.id === quizArg.quizId));
    },
    [localQuizes, isDialogOpen, setQuizAction, setQuizInfo],
  );

  const closeQuizDialog = () => {
    setIsDialogOpen(false);
    setQuizInfo({});
  };

  const handleCreateQuiz = async (quizData) => {
    handleToggleDialog(null);

    try {
      const result = await createQuiz({ ...quizData, courseId: course.id, quizPages: quizPages, settings: course.settings });
      const updatedCourse = result?.course || result;
      if (updatedCourse?.id) {
        setCourseState(updatedCourse);
      }
      setHasLocalChanges(false);
      setHasQuiz(true);
    } catch (error) {
      console.error('Failed to create quiz:', error);
    }
  };

  const handleResetUserQuiz = async () => {
    await resetUserQuiz({ userId: userToReset.id, quiz: activeQuiz, courseId: course.id });
    setUserToReset(null);
  };

  const handleDeleteQuiz = async () => {
    setHasLocalChanges(true);
    try {
      await deleteQuizByPage({ quizId: quizPageToDelete.id, courseId: course.id });
      setLocalQuizes((prev) => {
        const updated = prev.filter((q) => q.id !== quizPageToDelete.id);
        return updated;
      });

      setQuizPageToDelete(null);
    } catch (error) {
      console.error('Failed to delete quiz:', error);
    }
  };

  const updateQuizProgress = useCallback(
    (e) => {
      setUseQuizProgress(e.target.checked);
      const courseData = {
        description: course.description,
        categories: course.categories,
        id: course.id,
        settings: {
          ...course.settings,
          quiz: {
            ...course.settings.quiz,
            diplomaByQuizProgress: e.target.checked,
          },
        },
      };
      updateCourse({ courseData });
    },
    [course, updateCourse],
  );

  const activateQuizStats = (quiz) => {
    if (quiz.usersCount > 0) {
      setActiveQuiz(quiz);
      setTableInfoWithQuiz(quiz);
    }
  };

  const saveQuizEdit = useCallback(
    (quizEditData) => {
      setLocalQuizes((prev) => {
        const updated = prev.map((quiz) => (quiz.id === quizInfo.id ? { ...quiz, ...quizEditData } : quiz));
        return updated;
      });
      setHasLocalChanges(true);

      updateQuizSettings({
        course,
        pageId: quizInfo.id,
        settings: quizEditData,
      });
    },
    [updateQuizSettings, quizInfo, course],
  );

  // Set active quiz and table info
  useEffect(() => {
    if (localQuizes.length && localQuizes[0].usersCount) {
      setActiveQuiz(localQuizes[0]);
      setTableInfoWithQuiz(localQuizes[0]);
    } else {
      setActiveQuiz(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuizes]); // Only depend on localQuizes, not course or setTableInfoWithQuiz

  return (
    <>
      <SubPage
        staticElements={
          <PageControls
            openModelButton={
              <Button icon='Edit' onClick={() => handleToggleDialog({ type: 'create' })}>
                {t('course.createQuiz')}
              </Button>
            }
          />
        }
      >
        <Flex align='center' justify='center' width='100%' maxWidth='600px'>
          <SwitchRow
            label={t('course.useQuizForDiploma')}
            checked={useQuizProgress}
            onChange={updateQuizProgress}
            enabledText={t('globals.enabled')}
            disabledText={t('globals.disabled')}
          />
        </Flex>
        {!hasQuiz ? (
          <Flex>
            <Typo.TextTitle>{t('course.noQuizInCourse')}</Typo.TextTitle>
          </Flex>
        ) : (
          <QuizCardsContainer>
            {localQuizes.map((quiz) => (
              <Content
                key={quiz.id}
                $hasAction={!!quiz.usersCount && localQuizes.length > 1}
                onClick={() => activateQuizStats(quiz)}
                selected={quiz.id === activeQuiz?.id}
              >
                <Flex>
                  <Typo.ContentSmallHeader $textAlign='left'>{quiz.name}</Typo.ContentSmallHeader>
                  <Icon.Edit size='x-small' onClick={() => handleToggleDialog({ quizId: quiz.id, type: 'edit' })} />
                </Flex>
                <QuizInfo>
                  <Typo.SmallText>
                    {t('course.quizUserPassed')} {quiz.totalPassed || 0} ({quiz.usersCount || 0})
                  </Typo.SmallText>
                  <Typo.SmallText>
                    {t('course.questions')} {quiz.tasks?.count}
                  </Typo.SmallText>
                  <Typo.SmallText>{quiz.id}</Typo.SmallText>
                  <Typo.SmallText>{!!quiz.repeatable ? t('globals.repeatable') : t('globals.notRepeatable')}</Typo.SmallText>
                </QuizInfo>
                <DeleteButton>
                  <IconAsButton
                    iconName='Delete'
                    variant='action'
                    placement='right'
                    color={theme.colors.ACTION}
                    clickHandle={() => setQuizPageToDelete(quiz)}
                  />
                </DeleteButton>
              </Content>
            ))}
          </QuizCardsContainer>
        )}
        {activeQuiz && (
          <Flex width='100%' direction='column'>
            <Typo.TextTitle $marginBottom='30px' $textAlign='left'>
              {activeQuiz.name}
            </Typo.TextTitle>

            <ListTable
              tableInfo={tableInfo}
              data={activeQuiz.users}
              onRowClick={setUserAnswers}
              tableName={'courseQuiz'}
              tableSetter={setTableInfo}
              additionalData={activeQuiz}
              listDataProp={'users'}
            />
          </Flex>
        )}

        {/*
          <div>Use quiz for progress - updates course wiht diplomaByQuizProgress</div>
          <div>Enable quiz - creates a section and a page named quiz, update course with quiz enabled and page just created </div>
          <div>Allows users to retake quiz - updates course page with repeatable </div>
          <div>Hide answers when quiz completed - updates course page with hideAnswers</div>
        */}

        <QuizEdit
          isDialogOpen={isDialogOpen}
          quizInfo={quizInfo}
          closeDialog={closeQuizDialog}
          onSave={quizAction === 'edit' ? saveQuizEdit : handleCreateQuiz}
        />
        <QuizResultsModal userAnswers={userAnswers} questions={activeQuiz?.questions} onClose={() => setUserAnswers(null)} />
        <ConfirmDelete
          onClose={setUserToReset}
          onAcceptDelete={handleResetUserQuiz}
          open={!!userToReset?.id}
          description={t('course.clearQuizResultsWarning', { userToReset: userToReset?.name })}
        />
        <ConfirmDelete
          onClose={setQuizPageToDelete}
          onAcceptDelete={handleDeleteQuiz}
          open={!!quizPageToDelete?.id}
          description={t('course.confirmQuizDelete', { course: course?.name })}
        />
      </SubPage>
    </>
  );
};

export default CourseQuiz;
